// src/services/salesOrderCreation.ts
// ---------------------------------
// Orchestrates creating Unleashed Sales Orders for a request at Confirm & Send:
// load the lookups (so_rules pipeline, product map, uniform supplier meta, the DFV
// Service warehouse, existing per-suffix counts), run the pure soRulesEngine, then
// write each resulting SO's flat `request_sales_orders` lines + a `queued`
// `request_sales_order_pushes` row (carrying suffix / sequence / payee). Anything
// the rules didn't claim lands on the engine's `ZZZ` SO and is audited (+ alert).
//
// Reference: docs/so-rules-engine.md.

import { getById as getDeliveryAddressById } from "../data/deliveryAddresses";
import { getConfigValues } from "../data/portalConfig";
import { createQueuedPush, suffixCountsForRequest } from "../data/salesOrderPushes";
import { createSalesOrder, writeLinePickerFields, writeLineSoNumbers } from "../data/salesOrders";
import { getShelterSalesMeta } from "../data/shelters";
import { listSoRules } from "../data/soRules";
import { listUniformSupplierMeta } from "../data/suppliers";
import { listProductMap } from "../data/unleashedProductMap";
import { createPickerTask, sendEmail } from "../utils/actions";
import { logAudit } from "../utils/auditLog";
import { logger } from "../utils/logger";
import { FIX_IT_CODE } from "./orderItemKinds";
import {
  type BuiltSO,
  buildSalesOrders,
  DEFAULT_SO_COMMENTS_TEMPLATE,
  DEFAULT_SO_CUSTOMER_REFERENCE,
  type RequestForEngine,
  renderSoTemplate,
  type SoRule,
} from "./soRulesEngine";

export interface QueueResult {
  created: number;
  orderNumbers: string[];
  unrouted: number;
  shortfall: number; // Σ(approved) − Σ(SO lines); non-zero = money was dropped
}

// Request-level fields the comments / customer-reference templates read (in
// addition to the per-SO child codes + schools the engine computes).
export interface SalesOrderRequestMeta {
  id: number;
  shelterCode?: string | null;
  shelterName?: string | null;
  contactName?: string | null; // caseworker name
  shelterEmail?: string | null; // caseworker email
  phone?: string | null; // caseworker mobile
  deliveryAddress?: string | null; // resolved one-line text (fallback / comments)
  deliveryAddressId?: string | null; // FK to delivery_addresses (structured block)
  deliveryAddressOverride?: string | null; // one-off address text (no structured row)
  salesPersonEmail?: string | null; // the responder who confirmed the order
}

// Map a picker-processed SO to the picker task it needs. Devices + QLD Stationery
// are the only picker types today; a requiresPicker SO is homogeneous (devices and
// stationery never share an SO), so the item kind determines the task type. The
// description lists one "Child N - Year X" label per distinct child on the SO, in
// child order (matching the request-view numbering). This makes the picker-task
// granularity FOLLOW the SO grouping (John, 26 Jul): a per_child stationery rule
// yields a one-child task per pack; a per_request device rule yields one multi-child
// task — change a rule's group_by and the tasks track it automatically.
function pickerTaskForOrder(o: BuiltSO): { type: string; description: string } | null {
  const hasDevice = o.items.some((it) => it.kind === "device");
  const hasStationery = o.items.some((it) => it.kind === "fee" && it.feeType === "Stationery");
  const type = hasDevice ? "device" : hasStationery ? "stationery_qld" : null;
  if (!type) return null;
  const seen = new Set<number>();
  const labels: string[] = [];
  for (const it of [...o.items].sort((a, b) => a.childIndex - b.childIndex)) {
    if (seen.has(it.childIndex)) continue;
    seen.add(it.childIndex);
    labels.push(`Child ${it.childIndex + 1}${it.yearLevel ? ` - ${it.yearLevel}` : ""}`);
  }
  return { type, description: labels.join("\n") };
}

// Build + queue the Sales Orders for a request's Confirm & Send. Best-effort — the
// caller (confirmAndSend) must never fail because of this.
export async function queueSalesOrderForRequest(
  req: RequestForEngine & SalesOrderRequestMeta,
  confirmedAt?: string
): Promise<QueueResult> {
  const [mapRows, supplierMeta, shelterMeta, rules, suffixCounts, templateCfg, deliveryRow] =
    await Promise.all([
      listProductMap(),
      listUniformSupplierMeta(),
      getShelterSalesMeta(req.shelterCode ?? undefined),
      listSoRules(),
      suffixCountsForRequest(req.id),
      getConfigValues(["so_comments_template", "so_customer_reference_template"]),
      getDeliveryAddressById(req.deliveryAddressId),
    ]);
  const warehouse = shelterMeta.default_warehouse;

  // Structured delivery block for the SO header. Prefer the request's registered
  // delivery_addresses row; fall back to the one-off override text (street only)
  // when there's no structured row. Suburb/City alignment (db-87): delivery_addresses
  // now has a displayed locality (`suburb_city`) plus the legacy `suburb`. The SO's
  // DeliveryCity carries the locality (suburb_city, fallback suburb); DeliverySuburb
  // carries the raw `suburb` (empty post-Unleashed-cleanup) for the lossless push.
  const deliveryHeader = deliveryRow
    ? {
        delivery_contact: req.contactName ?? null,
        delivery_name: deliveryRow.address_name ?? req.shelterName ?? null,
        delivery_street_address: deliveryRow.street ?? null,
        delivery_street_address_2: deliveryRow.street2 ?? null,
        delivery_suburb: deliveryRow.suburb ?? null,
        delivery_city: deliveryRow.suburb_city ?? deliveryRow.suburb ?? null,
        delivery_region: deliveryRow.state ?? null,
        delivery_post_code: deliveryRow.postcode ?? null,
        delivery_country: "Australia",
        delivery_instruction: deliveryRow.delivery_instruction ?? null,
      }
    : {
        delivery_contact: req.contactName ?? null,
        delivery_name: req.shelterName ?? null,
        delivery_street_address: req.deliveryAddressOverride ?? req.deliveryAddress ?? null,
        delivery_country: "Australia",
      };

  // Order Date = the Confirm & Send date (YYYY-MM-DD); Required Date + Quote Expiry
  // are set to the same date (John, 13 Jul). Guard against a non-date confirmedAt.
  const parsedDate = new Date(confirmedAt || Date.now());
  const orderDate = (Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate)
    .toISOString()
    .slice(0, 10);

  // Resolve the (editable) comments / customer-reference templates, falling back
  // to the built-in defaults when a key is absent.
  const cfg = new Map<string, unknown>((templateCfg ?? []).map((r) => [r.key, r.value]));
  const commentsTemplate =
    typeof cfg.get("so_comments_template") === "string"
      ? (cfg.get("so_comments_template") as string)
      : DEFAULT_SO_COMMENTS_TEMPLATE;
  const customerRefTemplate =
    typeof cfg.get("so_customer_reference_template") === "string"
      ? (cfg.get("so_customer_reference_template") as string)
      : DEFAULT_SO_CUSTOMER_REFERENCE;

  // Resolvers for the pure engine.
  // Year-aware product map (db-115): keyed item_kind|item_key|year_bucket ('' = base
  // row). The resolver tries the year bucket then falls back to the base row.
  const map = new Map<string, string>();
  for (const r of mapRows ?? []) {
    const yb = (r as { year_bucket?: string | null }).year_bucket ?? "";
    map.set(`${r.item_kind}|${r.item_key}|${yb}`, r.product_code);
  }
  const uniCode = new Map<string, string | null>();
  const metaByName = new Map<
    string,
    { so_suffix: string | null; kind: string | null; payment_method: string | null }
  >();
  for (const s of supplierMeta ?? []) {
    uniCode.set(s.name, s.unleashed_product_code);
    metaByName.set(s.name, {
      so_suffix: s.so_suffix,
      kind: s.kind,
      payment_method: s.payment_method,
    });
  }

  const { orders, zzzUnrouted, expectedTotal, builtTotal, unmapped } = buildSalesOrders(
    req,
    (rules ?? []) as unknown as SoRule[],
    {
      // Stationery refines by year (db-115): try (kind|key|yearBucket), else the base row.
      //
      // FIX_IT is the "not really mapped yet" sentinel (orderItemKinds.ts). It is a real
      // row in unleashed_product_map, so a truthiness check inside the engine can't tell it
      // from a genuine code — it used to build a full-value SO line that Unleashed rejects
      // with a 400, AND it defeated both safety nets on the way out: the line carried its
      // full value so Σ(lines) still reconciled, and the item HAD matched a rule so it never
      // reached the ZZZ net. Nothing alerted. Filtering it here (rather than in the engine,
      // which stays pure and sentinel-agnostic) makes the engine treat it as unmapped: the
      // line is dropped, builtTotal falls short of expectedTotal, and the existing
      // money-reconciliation alert fires with the item named under unmappedItems. Same path
      // the unpriced-uniform-supplier case already takes. Finding B16/B9, 18 Aug 2026.
      productCode: (kind, key, yb) => {
        const code = map.get(`${kind}|${key}|${yb ?? ""}`) ?? map.get(`${kind}|${key}|`);
        return code === FIX_IT_CODE ? undefined : code;
      },
      uniformProductCode: (name) => uniCode.get(name) ?? undefined,
      existingSuffixCount: (suffix) => suffixCounts[suffix] ?? 0,
    },
    { confirmedAt, supplierMeta: (name) => metaByName.get(name) ?? null }
  );

  const written: string[] = [];
  for (const o of orders) {
    // The per-SO template context: child codes + schools scoped to THIS SO, plus
    // the request-level caseworker / delivery / service fields.
    const ctx = {
      childCodes: o.childCodes,
      schools: o.schools,
      caseworkerName: req.contactName,
      caseworkerEmail: req.shelterEmail,
      caseworkerMobile: req.phone,
      delivery: req.deliveryAddress,
      dfvService: req.shelterName,
      refNo: req.refNo,
      suffix: o.suffix,
    };
    const comments = renderSoTemplate(commentsTemplate, ctx);
    // Per-rule Customer Reference (db-78): render THIS SO's own template (the highest-priority
    // contributing rule's), falling back to the global template only when that rule left it
    // blank. No request-number fallback — the template yields at least the type label, and
    // school is guaranteed at capture (docs/so-reference-and-child-ref-plan.md §0).
    const soRefTemplate = o.customerReferenceTemplate ?? customerRefTemplate;
    const customerRef = renderSoTemplate(soRefTemplate, ctx) || null;
    const header = {
      order_number: o.order_number,
      customer_code: req.shelterCode ?? "",
      customer_reference: customerRef,
      warehouse_code: warehouse,
      comments: comments || null,
      order_date: orderDate,
      required_date: orderDate,
      quote_expiry_date: orderDate,
      sales_order_group: shelterMeta.sales_group,
      sales_person_email: req.salesPersonEmail ?? null,
      ...deliveryHeader,
    };
    const { error } = await createSalesOrder(header, o.lines, req.id);
    if (error) {
      logger.warn("queueSalesOrderForRequest: createSalesOrder failed:", error.message);
      continue;
    }
    await createQueuedPush(o.order_number, req.id, {
      suffix: o.suffix,
      suffix_sequence: o.sequence,
      payee_identifier: o.payee_identifier,
      // Picker-processed SOs (Devices / QLD Stationery) are parked on 'hold' (db-113)
      // until the picker fixes the price / product code / order number and releases them.
      status: o.requiresPicker ? "hold" : "queued",
    });
    // One picker task per picker-processed SO, so the task granularity mirrors the SO
    // grouping (per_child rule → a task per child pack; per_request → one multi-child task).
    if (o.requiresPicker) {
      const pt = pickerTaskForOrder(o);
      if (pt) {
        createPickerTask(pt.type, {
          requestId: req.id,
          refNo: req.refNo ?? "",
          shelterName: req.shelterName ?? "",
          shelterCode: req.shelterCode ?? "",
          state: req.state ?? "",
          description: pt.description,
          // The SO this task fulfils — scopes the picker view / release / completion (db-118).
          soNumber: o.order_number,
        });
      }
    }
    written.push(o.order_number);
  }

  if (written.length > 0) {
    logAudit(
      "sales_orders_queued",
      { request_id: req.id, count: written.length, order_numbers: written },
      req.id
    );
  }

  // Stamp the nominated SO number (`<ref>-<SUFFIX>[seq]`) onto each device/fee line
  // record it covers, so it shows on the request + the invoicing team can match on it.
  // (The SO number is OURS — no Unleashed round-trip. `*_order_number` is a separate,
  // hand-entered supplier-confirmation field and is NOT touched here.) Best-effort.
  const deviceWrites: { id: string; soNumber: string }[] = [];
  const feeWrites: { id: string; soNumber: string }[] = [];
  // Seed each device child's product code with the engine-resolved code (e.g. DEV_LAP)
  // so responders see it right after approval; the picker later overwrites it via the
  // Product picker. Codes are the SO's non-DISCOUNT lines in order (an aggregate device
  // SO shares the single code across its device items).
  const deviceProductSeeds: { id: string; productCode: string | null }[] = [];
  for (const o of orders) {
    const codes = (o.lines || [])
      .filter((l) => l.product_code && l.product_code !== "DISCOUNT")
      .map((l) => l.product_code);
    let devIdx = 0;
    for (const it of o.items) {
      if (!it.id) continue;
      if (it.kind === "device") {
        deviceWrites.push({ id: String(it.id), soNumber: o.order_number });
        const code = codes[devIdx] ?? (codes.length === 1 ? codes[0] : null);
        deviceProductSeeds.push({ id: String(it.id), productCode: code ?? null });
        devIdx += 1;
      } else if (it.kind === "fee") {
        feeWrites.push({ id: String(it.id), soNumber: o.order_number });
      }
    }
  }
  if (deviceWrites.length > 0 || feeWrites.length > 0) {
    await writeLineSoNumbers(deviceWrites, feeWrites);
  }
  if (deviceProductSeeds.length > 0) {
    await writeLinePickerFields(deviceProductSeeds, []);
  }
  // Reconciliation + safety-net alert (docs/so-rules-engine.md §3.5). We MUST always
  // send Unleashed the full approved total, so we alert whenever EITHER:
  //   (a) a ZZZ SO was created — one or more items matched no rule (routing gap), OR
  //   (b) the money doesn't reconcile — Σ(approved) ≠ Σ(SO lines), which happens when
  //       an item matched a rule but couldn't be lined (e.g. a uniform supplier with no
  //       Unleashed product code — the 2026-00155 case).
  // The recipient is resolved server-side by the send-email Edge Function (portal_config
  // key `system_alert_email`). Best-effort — never blocks the confirm.
  const shortfall = Math.round((expectedTotal - builtTotal) * 100) / 100;
  const moneyMismatch = Math.abs(shortfall) >= 0.01;
  if (zzzUnrouted.length > 0 || moneyMismatch) {
    const unroutedItems = zzzUnrouted.map((i) => ({
      kind: i.kind,
      key: i.feeType ?? i.deviceType ?? i.shoeKind ?? i.supplier ?? "",
      child: i.childCode,
    }));
    // Routed-but-unlined items (missing product code) — the usual mismatch cause.
    const unmappedItems = unmapped.map((u) => ({ kind: u.kind, key: u.key, child: u.childCode }));
    logAudit(
      "sales_order_reconcile_alert",
      {
        request_id: req.id,
        expected_total: expectedTotal,
        built_total: builtTotal,
        shortfall,
        unrouted: unroutedItems,
        unmapped: unmappedItems,
      },
      req.id
    );
    sendEmail("sales_order_unrouted", {
      refNo: req.refNo ?? null,
      requestId: req.id,
      shelterName: req.shelterName ?? null,
      // The ZZZ order only exists when a rule matched nothing; a pure money mismatch has none.
      orderNumber: zzzUnrouted.length > 0 ? `${req.refNo ?? ""}-ZZZ` : null,
      expectedTotal,
      builtTotal,
      shortfall,
      items: unroutedItems,
      unmappedItems,
    });
  }

  return {
    created: written.length,
    orderNumbers: written,
    unrouted: zzzUnrouted.length,
    shortfall,
  };
}
